import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getData, postData } from '../../services/api';
import AuthProductBrand from '../../components/auth/AuthProductBrand';
import AuthAppDownload from '../../components/auth/AuthAppDownload';

const INDUSTRY_ICONS = {
  education:          'bi-mortarboard-fill',
  training_institute: 'bi-journal-richtext',
  hospital:           'bi-heart-pulse-fill',
  software_company:   'bi-code-slash',
  retail:             'bi-shop-window',
  manufacturing:      'bi-gear-fill',
  corporate:          'bi-building-fill',
};

export default function Login({ returnTo }) {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const [searchParams] = useSearchParams();

  // ?tenant= or ?org= in URL → scope login to that tenant
  const orgFromUrl = searchParams.get('tenant') || searchParams.get('org') || '';

  // Resolve tenant name from slug for display banner
  const [orgTenantInfo, setOrgTenantInfo] = useState(null);
  useEffect(() => {
    if (!orgFromUrl) return;
    getData(`/auth/resolve-tenant?slug=${encodeURIComponent(orgFromUrl)}`)
      .then(r => setOrgTenantInfo(r?.data || r))
      .catch(() => {});
  }, [orgFromUrl]); // eslint-disable-line

  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetForm, setResetForm] = useState({ email: '', otp: '', newPassword: '' });
  const [resetStep, setResetStep] = useState(1);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');

  // ── Tenant picker (when same email in multiple orgs) ────────────────────
  const [tenantPicker, setTenantPicker] = useState(null); // { tenants: [...] }
  const [selectedTenantId, setSelectedTenantId] = useState('');

  // ── Demo mode ───────────────────────────────────────────────────────────
  const [demoMode, setDemoMode] = useState(false);
  const [demoTenants, setDemoTenants] = useState([]);
  const [demoLoading, setDemoLoading] = useState(false);
  const [selectedDemo, setSelectedDemo] = useState(null);

  // ── Demo availability from backend ──────────────────────────────────────
  const [demoAvailable, setDemoAvailable] = useState({ superAdmin: false, industry: false });
  useEffect(() => {
    getData('/config/demo-mode')
      .then(r => {
        const d = r?.data || r;
        setDemoAvailable({ superAdmin: d?.superAdmin || false, industry: d?.industry || false });
      })
      .catch(() => {});
  }, []);

  // Load demo tenants when demo panel opens
  useEffect(() => {
    if (demoMode && demoTenants.length === 0) {
      setDemoLoading(true);
      getData('/auth/demo-tenants')
        .then(r => setDemoTenants(Array.isArray(r) ? r : r?.data || r?.records || []))
        .catch(() => setDemoTenants([]))
        .finally(() => setDemoLoading(false));
    }
  }, [demoMode]); // eslint-disable-line

  const isAnyDemoAvailable = demoAvailable.superAdmin || demoAvailable.industry;

  const handleSelectDemo = (t) => {
    setSelectedDemo(t);
    setForm({ email: t.email, password: t.password });
    setLoginError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    if (!form.email || !form.password) { setLoginError('Email and password are required.'); return; }
    setLoading(true);
    try {
      const payload = { email: form.email, password: form.password };
      if (selectedTenantId) payload.tenantId = selectedTenantId;
      if (orgFromUrl && !selectedTenantId) payload.orgSlug = orgFromUrl;

      const data = await login(payload.email, payload.password, payload.orgSlug, payload.tenantId);

      // Backend asks user to pick a tenant
      if (data?.requireTenantSelection) {
        setTenantPicker(data);
        setLoading(false);
        return;
      }

      if (returnTo && returnTo.startsWith('/website-blog/authorize')) navigate(returnTo);
      else if (data?.user?.userType === 'super_admin') navigate('/sa/dashboard');
      else navigate('/dashboard');
    } catch (err) {
      const msg = err.response?.data?.message || 'Login failed. Please check your credentials.';
      // If the error response contains requireTenantSelection (some backends return it differently)
      if (err.response?.data?.data?.requireTenantSelection) {
        setTenantPicker(err.response.data.data);
      } else {
        setLoginError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTenantSelect = async (tenantId) => {
    setSelectedTenantId(tenantId);
    setTenantPicker(null);
    setLoading(true);
    setLoginError('');
    try {
      const data = await login(form.email, form.password, undefined, tenantId);
      if (returnTo && returnTo.startsWith('/website-blog/authorize')) navigate(returnTo);
      else if (data?.user?.userType === 'super_admin') navigate('/sa/dashboard');
      else navigate('/dashboard');
    } catch (err) {
      setLoginError(err.response?.data?.message || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestResetOtp = async () => {
    if (!resetForm.email) { setResetMessage('Enter your email address.'); return; }
    setResetLoading(true);
    setResetMessage('');
    try {
      await postData('/auth/request-password-reset-otp', { email: resetForm.email });
      setResetStep(2);
      setResetMessage('OTP sent to your email.');
    } catch (err) {
      setResetMessage(err.response?.data?.message || 'Failed to send OTP.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleVerifyResetOtp = async () => {
    if (!resetForm.otp) { setResetMessage('Enter the OTP sent to your email.'); return; }
    setResetLoading(true);
    setResetMessage('');
    try {
      await postData('/auth/verify-password-reset-otp', { email: resetForm.email, otp: resetForm.otp });
      setResetStep(3);
      setResetMessage('OTP verified. Set your new password.');
    } catch (err) {
      setResetMessage(err.response?.data?.message || 'Failed to verify OTP.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetForm.newPassword || resetForm.newPassword.length < 6) {
      setResetMessage('New password must be at least 6 characters.');
      return;
    }
    setResetLoading(true);
    setResetMessage('');
    try {
      await postData('/auth/reset-password-with-otp', {
        email: resetForm.email,
        newPassword: resetForm.newPassword,
      });
      setResetMessage('Password reset successfully. You can now sign in.');
      setShowReset(false);
      setResetStep(1);
      setResetForm({ email: '', otp: '', newPassword: '' });
    } catch (err) {
      setResetMessage(err.response?.data?.message || 'Failed to reset password.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="auth-glass">
      <AuthProductBrand />
      <h4 className="text-center fw-bold mb-1">Welcome Back</h4>
      <p className="text-center mb-4" style={{ fontSize: 13 }}>
        Continue learning with Skill Share
      </p>

      {/* Org context banner — shown when URL has ?tenant=slug */}
      {orgTenantInfo && (
        <div className="mb-3 px-3 py-2 d-flex align-items-center gap-2"
          style={{ background: 'rgba(99,102,241,0.18)', border: '1px solid rgba(196,181,253,0.45)', borderRadius: 11, fontSize: 13 }}>
          <i className="bi bi-buildings-fill" style={{ color: '#c4b5fd' }}></i>
          <div>
            <span style={{ fontSize: 11, opacity: 0.7 }}>Signing into</span>
            <div className="fw-semibold" style={{ lineHeight: 1.2 }}>{orgTenantInfo.name}</div>
          </div>
        </div>
      )}

      {/* Demo Mode Banner — hidden when scoped to a tenant */}
      {!orgFromUrl && (demoAvailable.superAdmin || demoAvailable.industry) && (
        <div className="text-center mb-3">
          <button
            type="button"
            className={`btn btn-sm ${demoMode ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={() => { setDemoMode(p => !p); setSelectedDemo(null); setLoginError(''); setForm({ email: '', password: '' }); }}
            style={{ fontSize: 12, fontWeight: 600, borderRadius: 20, padding: '5px 16px' }}
          >
            <i className={`bi ${demoMode ? 'bi-x-circle' : 'bi-play-circle-fill'} me-1`}></i>
            {demoMode ? 'Exit Demo' : 'Try Demo'}
          </button>
        </div>
      )}

      {/* Demo Tenant Grid */}
      {!orgFromUrl && demoMode && demoAvailable.industry && (
        <div className="mb-3">
          <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12, padding: '14px' }}>
            <p className="text-center mb-2" style={{ fontSize: 11, color: '#fcd34d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
              <i className="bi bi-grid me-1"></i>Select an industry to explore
            </p>
            {demoLoading ? (
              <div className="text-center py-3">
                <span className="spinner-border spinner-border-sm text-warning"></span>
                <div style={{ fontSize: 12, color: '#fcd34d', marginTop: 6 }}>Loading demos...</div>
              </div>
            ) : demoTenants.length === 0 ? (
              <p className="text-center mb-0" style={{ fontSize: 12, color: 'rgba(248,250,252,0.6)' }}>
                No demo organizations available. Run <code>npm run seed</code> first.
              </p>
            ) : (
              <div className="row g-2">
                {demoTenants.map(tenant => {
                  const slug = tenant.industry?.slug || '';
                  const icon  = INDUSTRY_ICONS[slug] || 'bi-grid';
                  const color = tenant.branding?.primaryColor || tenant.industry?.color || '#a78bfa';
                  const isSelected = selectedDemo?.slug === tenant.slug;
                  return (
                    <div key={tenant.slug} className="col-6">
                      <button type="button" onClick={() => handleSelectDemo(tenant)} style={{
                        width: '100%',
                        background: isSelected ? `linear-gradient(135deg, ${color}, ${color}cc)` : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${isSelected ? color : 'rgba(255,255,255,0.14)'}`,
                        borderRadius: 10,
                        padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 9,
                        cursor: 'pointer', transition: 'all 0.15s',
                        color: isSelected ? '#fff' : '#f8fafc',
                        textAlign: 'left',
                        boxShadow: isSelected ? `0 6px 20px ${color}66` : 'none',
                      }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                          background: isSelected ? 'rgba(255,255,255,0.22)' : `${color}28`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className={`bi ${icon}`} style={{ fontSize: 15, color: isSelected ? '#fff' : color }}></i>
                        </div>
                        <div style={{ lineHeight: 1.25, minWidth: 0, overflow: 'hidden' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{tenant.name}</div>
                          <div style={{ fontSize: 10.5, opacity: 0.75 }}>{tenant.industry?.name || slug}</div>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Selected demo badge */}
      {demoMode && selectedDemo && (
        <div className="d-flex align-items-center gap-2 mb-3 px-3 py-2"
          style={{ background: 'rgba(34,197,94,0.18)', border: '1px solid rgba(134,239,172,0.45)', borderRadius: 11, fontSize: 12.5, color: '#bbf7d0' }}>
          <i className="bi bi-check-circle-fill" style={{ color: '#4ade80' }}></i>
          <span>Exploring <strong>{selectedDemo.name}</strong> ({selectedDemo.industry?.name})</span>
        </div>
      )}

      {/* Tenant Picker Dialog */}
      {tenantPicker && (
        <div className="mb-3 p-3 rounded" style={{ background: 'rgba(252,211,77,0.10)', border: '1px solid rgba(252,211,77,0.35)' }}>
          <p className="fw-semibold mb-2" style={{ fontSize: 13 }}>
            <i className="bi bi-buildings me-1"></i>
            This email belongs to multiple organizations. Select one:
          </p>
          {tenantPicker.tenants.map(t => (
            <button key={t.id} type="button"
              className="pick-card w-100 text-start mb-1 py-2 px-3"
              style={{ fontSize: 13 }}
              onClick={() => handleTenantSelect(t.id)}>
              <i className="bi bi-building me-2" style={{ color: '#c4b5fd' }}></i>
              <strong>{t.name}</strong>
              {t.industry && <span className="ms-2" style={{ fontSize: 11, opacity: 0.7 }}>({t.industry.name})</span>}
            </button>
          ))}
          <button type="button" className="btn btn-sm btn-outline-secondary mt-1"
            onClick={() => setTenantPicker(null)}>Cancel</button>
        </div>
      )}

      {/* Error */}
      {loginError && (
        <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: 13, borderRadius: 9 }}>
          <i className="bi bi-exclamation-circle-fill"></i>{loginError}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Email Address</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-envelope"></i></span>
            <input type="email" className="form-control"
              placeholder="you@example.com"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              required
              readOnly={demoMode && !!selectedDemo}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label">Password</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-lock"></i></span>
            <input
              type={showPass ? 'text' : 'password'}
              className="form-control"
              placeholder="••••••••"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              required
              readOnly={demoMode && !!selectedDemo}
            />
            <button type="button" className="input-group-text" style={{ cursor: 'pointer' }}
              onClick={() => setShowPass(p => !p)}>
              <i className={`bi ${showPass ? 'bi-eye-slash' : 'bi-eye'}`}></i>
            </button>
          </div>
          <div className="d-flex justify-content-end mt-2">
            <button
              type="button"
              className="btn btn-link p-0"
              style={{ fontSize: 12, color: '#f5d0fe', textDecoration: 'none' }}
              onClick={() => {
                setShowReset(true);
                setResetStep(1);
                setResetMessage('');
                setResetForm({ email: form.email || '', otp: '', newPassword: '' });
              }}
            >
              Forgot password?
            </button>
          </div>
        </div>

        <button type="submit" className="btn btn-primary w-100"
          style={{ padding: '10px', fontWeight: 700, fontSize: 14 }}
          disabled={loading || (demoMode && !selectedDemo)}>
          {loading
            ? <><span className="spinner-border spinner-border-sm me-2"></span>Signing in...</>
            : demoMode
              ? selectedDemo
                ? <><i className="bi bi-play-fill me-1"></i>Explore {selectedDemo.industry?.name || 'Demo'}</>
                : 'Select an industry above'
              : <><i className="bi bi-box-arrow-in-right me-1"></i>Sign In</>
          }
        </button>
      </form>

      {/* Self-registration — always available, tenant-scoped when in tenant context */}
      <div className="text-center mt-3 py-2 px-3"
        style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 11, border: '1px solid rgba(255,255,255,0.12)', fontSize: 13 }}>
        <i className="bi bi-person-plus me-1" style={{ color: '#c4b5fd' }}></i>
        <span style={{ opacity: 0.7 }}>New member? </span>
        <Link
          to={orgFromUrl ? `/register/user?tenant=${encodeURIComponent(orgFromUrl)}` : '/register/user'}
          className="fw-semibold"
        >
          Create an account
        </Link>
      </div>

      <AuthAppDownload mobile />

      {showReset && (
        <div className="mt-3 p-3" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)' }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong style={{ fontSize: 13 }}>Reset Password</strong>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowReset(false)}>Close</button>
          </div>
          {resetMessage && <div className="alert alert-info py-2 mb-2" style={{ fontSize: 12 }}>{resetMessage}</div>}
          <div className="mb-2">
            <label className="form-label" style={{ fontSize: 12 }}>Email</label>
            <input className="form-control" value={resetForm.email} onChange={e => setResetForm(f => ({ ...f, email: e.target.value }))} />
          </div>
          {resetStep >= 2 && (
            <div className="mb-2">
              <label className="form-label" style={{ fontSize: 12 }}>OTP</label>
              <input className="form-control" value={resetForm.otp} onChange={e => setResetForm(f => ({ ...f, otp: e.target.value.replace(/\D/g, '').slice(0, 6) }))} />
            </div>
          )}
          {resetStep >= 3 && (
            <div className="mb-2">
              <label className="form-label" style={{ fontSize: 12 }}>New Password</label>
              <input type="password" className="form-control" value={resetForm.newPassword} onChange={e => setResetForm(f => ({ ...f, newPassword: e.target.value }))} />
            </div>
          )}
          <div className="d-flex gap-2">
            {resetStep === 1 && <button type="button" className="btn btn-primary btn-sm fw-semibold" style={{ minWidth: 118, color: '#fff' }} disabled={resetLoading} onClick={handleRequestResetOtp}>{resetLoading ? 'Sending...' : 'Send OTP'}</button>}
            {resetStep === 2 && <button type="button" className="btn btn-primary btn-sm fw-semibold" style={{ minWidth: 118, color: '#fff' }} disabled={resetLoading} onClick={handleVerifyResetOtp}>{resetLoading ? 'Verifying...' : 'Verify OTP'}</button>}
            {resetStep === 3 && <button type="button" className="btn btn-primary btn-sm fw-semibold" style={{ minWidth: 134, color: '#fff' }} disabled={resetLoading} onClick={handleResetPassword}>{resetLoading ? 'Resetting...' : 'Reset Password'}</button>}
          </div>
        </div>
      )}

      {/* Platform-level links — HIDDEN in tenant context */}
    </div>
  );
}
